"use client";

import { c as _c } from "react/compiler-runtime";
import { Calendar, MapPin, Euro, Hash, User, Building2 } from "lucide-react";
import type { DealWithRelations } from "@/lib/deals/types";
import { DealPositionsList } from "@/components/deals/DealPositionsList";
import { dealTotal, tradedPositions } from "@/lib/deals/positions";
import { dealQuantityLabel, positionSubject } from "@/lib/deals/position-labels";
interface TransactionSummaryProps {
  deal: DealWithRelations;
  role: "buyer" | "seller";
  className?: string;
}

/**
 * TransactionSummary - Displays a summary of the completed transaction
 */
export function TransactionSummary(t0) {
  const $ = _c(71);
  const {
    deal,
    role,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let counterparty;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== deal.buyer || $[2] !== deal.completed_at || $[3] !== deal.id || $[4] !== deal.offering || $[5] !== deal.positions || $[6] !== deal.seller || $[7] !== role) {
    const positions = deal.positions ?? [];
    const traded = tradedPositions(positions);
    const formatCurrency = _temp;
    const formatDate = _temp2;
    counterparty = role === "buyer" ? deal.seller : deal.buyer;
    t14 = `rounded-lg border border-zinc-200 bg-white ${className}`;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <div className="p-5 border-b border-zinc-100"><h3 className="font-semibold text-zinc-900">Transaktionsübersicht</h3></div>;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    t11 = "p-5 space-y-4";
    let t16;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="flex items-center gap-2 text-zinc-500 text-xs"><Hash className="w-3 h-3" />Transaktions-ID</div>;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    let t17;
    if ($[25] !== deal.id) {
      t17 = deal.id.slice(0, 8).toUpperCase();
      $[25] = deal.id;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    let t18;
    if ($[27] !== t17) {
      t18 = <div className="space-y-1">{t16}<p className="text-sm font-medium text-zinc-900 font-mono">{t17}</p></div>;
      $[27] = t17;
      $[28] = t18;
    } else {
      t18 = $[28];
    }
    let t19;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <div className="flex items-center gap-2 text-zinc-500 text-xs"><Calendar className="w-3 h-3" />Abgeschlossen am</div>;
      $[29] = t19;
    } else {
      t19 = $[29];
    }
    let t20;
    if ($[30] !== deal.completed_at) {
      t20 = formatDate(deal.completed_at);
      $[30] = deal.completed_at;
      $[31] = t20;
    } else {
      t20 = $[31];
    }
    let t21;
    if ($[32] !== t20) {
      t21 = <div className="space-y-1">{t19}<p className="text-sm font-medium text-zinc-900">{t20}</p></div>;
      $[32] = t20;
      $[33] = t21;
    } else {
      t21 = $[33];
    }
    if ($[34] !== t18 || $[35] !== t21) {
      t12 = <div className="grid grid-cols-2 gap-4">{t18}{t21}</div>;
      $[34] = t18;
      $[35] = t21;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    t13 = deal.offering && <div className="pt-4 border-t border-zinc-100 space-y-3"><h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Angebot</h4><div className="grid grid-cols-2 gap-4"><div className="space-y-1"><div className="flex items-center gap-2 text-zinc-500 text-xs"><MapPin className="w-3 h-3" />Standort</div><p className="text-sm font-medium text-zinc-900">{[deal.offering.gemeinde, deal.offering.landkreis, deal.offering.bundesland].filter(Boolean).join(", ") || "\u2014"}</p></div><div className="space-y-1"><p className="text-zinc-500 text-xs">Art</p><p className="text-sm font-medium text-zinc-900">{traded.length > 0 ? traded.map(positionSubject).join(" \xB7 ") : "\u2014"}</p></div></div></div>;
    t8 = "pt-4 border-t border-zinc-100 space-y-3";
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Finanzen</h4>;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    t10 = traded.length > 1 && <DealPositionsList positions={positions} />;
    t6 = "grid grid-cols-2 gap-4";
    let t22;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <p className="text-zinc-500 text-xs">Menge</p>;
      $[38] = t22;
    } else {
      t22 = $[38];
    }
    t7 = <div className="space-y-1">{t22}<p className="text-sm font-medium text-zinc-900">{dealQuantityLabel(positions) ?? "\u2014"}</p></div>;
    t4 = "space-y-1";
    let t23;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <Euro className="w-3 h-3" />;
      $[39] = t23;
    } else {
      t23 = $[39];
    }
    const t24 = role === "buyer" ? "Kaufpreis" : "Verkaufspreis";
    if ($[40] !== t24) {
      t5 = <div className="flex items-center gap-2 text-zinc-500 text-xs">{t23}{t24}</div>;
      $[40] = t24;
      $[41] = t5;
    } else {
      t5 = $[41];
    }
    t2 = "text-sm font-medium text-zinc-900";
    t3 = formatCurrency(dealTotal(positions));
    $[0] = className;
    $[1] = deal.buyer;
    $[2] = deal.completed_at;
    $[3] = deal.id;
    $[4] = deal.offering;
    $[5] = deal.positions;
    $[6] = deal.seller;
    $[7] = role;
    $[8] = counterparty;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    counterparty = $[8];
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
    t14 = $[13];
    t15 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t16;
  if ($[42] !== t2 || $[43] !== t3) {
    t16 = <p className={t2}>{t3}</p>;
    $[42] = t2;
    $[43] = t3;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t16 || $[46] !== t4 || $[47] !== t5) {
    t17 = <div className={t4}>{t5}{t16}</div>;
    $[45] = t16;
    $[46] = t4;
    $[47] = t5;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t17 || $[50] !== t6 || $[51] !== t7) {
    t18 = <div className={t6}>{t7}{t17}</div>;
    $[49] = t17;
    $[50] = t6;
    $[51] = t7;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t10 || $[54] !== t18 || $[55] !== t8 || $[56] !== t9) {
    t19 = <div className={t8}>{t9}{t10}{t18}</div>;
    $[53] = t10;
    $[54] = t18;
    $[55] = t8;
    $[56] = t9;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== counterparty || $[59] !== role) {
    t20 = counterparty && <div className="pt-4 border-t border-zinc-100 space-y-3"><h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{role === "buyer" ? "Verk\xE4ufer" : "K\xE4ufer"}</h4><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">{counterparty.company_name ? <Building2 className="w-5 h-5 text-zinc-500" /> : <User className="w-5 h-5 text-zinc-500" />}</div><div><p className="text-sm font-medium text-zinc-900">{counterparty.company_name || [counterparty.first_name, counterparty.last_name].filter(Boolean).join(" ") || "\u2014"}</p>{counterparty.company_name && (counterparty.first_name || counterparty.last_name) && <p className="text-xs text-zinc-500">{[counterparty.first_name, counterparty.last_name].filter(Boolean).join(" ")}</p>}</div></div></div>;
    $[58] = counterparty;
    $[59] = role;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t11 || $[62] !== t12 || $[63] !== t13 || $[64] !== t19 || $[65] !== t20) {
    t21 = <div className={t11}>{t12}{t13}{t19}{t20}</div>;
    $[61] = t11;
    $[62] = t12;
    $[63] = t13;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== t14 || $[68] !== t15 || $[69] !== t21) {
    t22 = <div className={t14}>{t15}{t21}</div>;
    $[67] = t14;
    $[68] = t15;
    $[69] = t21;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  return t22;
}
function _temp2(dateString) {
  if (!dateString) {
    return "\u2014";
  }
  return new Date(dateString).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
}
function _temp(amount) {
  if (amount == null) {
    return "\u2014";
  }
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
}